import { Row } from '../../../../@ui'
import {
  ArticleEditor,
  ArticleEditorRef,
  ContributionPosition,
  SelectionInfo,
} from '../article-editor/article-editor'
import { createStyle } from '../../../../contexts/global-styles.provider'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ContributionStatus,
  ContributionType,
} from '../../../../generated/graphql'
import { VerticalContributionsIndicator } from './components/vertica-contributors.indicator'
import { useTheme } from '../../../../hooks/theme.hook'
import { Article, Contribution } from './types'
import { ContributionDetails } from './components/contribution-details'
import { View } from 'react-native'
import { formatSelection } from './format'

const CONTRIBUTION_TYPE_MAP = {
  [ContributionType.Challenge]: 3,
  [ContributionType.Edit]: 1,
  [ContributionType.Inquiry]: 2,
  [ContributionType.Reserved]: 0,
  [ContributionType.OwnerEdit]: 4,
} as const

export type DisplayPerspectiveRef = {
  closeContributionModal: () => void
  clearSelection: () => void
  lockSelection: ({
    start,
    end,
  }: {
    start: number
    end: number
    html: string
  }) => void
  getEditorRef: () => ArticleEditorRef | null
}

export const DisplayPerspective = forwardRef(
  (
    {
      perspective,
      contributions,
      displayVerticalIndicator,
      verticalIndicatorOffset,
      readonly,
      refetch,
      setSelection,
      onContribution,
      onFullScreen,
      onEditorStateChange,
    }: {
      perspective?: Article
      contributions: Contribution[]
      // This is just for a hack needed to keep the component without scroll when
      // the vertical indicator is on and there is a modal open from the parent.
      verticalIndicatorOffset?: boolean
      displayVerticalIndicator: boolean
      readonly?: boolean
      refetch: () => void
      setSelection?: (selection: {
        start: number
        end: number
        html: string
      }) => void
      onContribution?: (contributionId: string | null) => void
      onFullScreen?: (contributionId: string) => void
      onEditorStateChange?: (state: {
        [action: string]: { active: boolean; enabled: boolean }
      }) => void
    },
    ref
  ) => {
    const editorRef = useRef<ArticleEditorRef>(null)
    const theme = useTheme()
    const styles = useStyles()

    // Contribution is selected on the editor and we display the modal
    const [editorContributionId, setEditorContributionId] = useState<string>('')

    /**
     * For the vertical indicator
     */
    const [contributionPositions, setContributionPositions] = useState<
      ContributionPosition[]
    >([])

    // The editor tell us the position of this contribution and we week it to pass it to the vertical indicator
    const [scrollPosition, setScrollPosition] = useState<{
      scrollTop: number
      scrollLeft: number
      viewportHeight: number
      contentHeight: number
    } | null>(null)

    /**
     * REFS
     */
    useImperativeHandle(ref, () => ({
      closeContributionModal: () => {
        setEditorContributionId('')
      },
      clearSelection: () => {
        editorRef.current?.lockSelection(false)
        editorRef.current?.clearSelection()
        setSelection?.({ start: 0, end: 0, html: '' })
      },
      lockSelection: ({
        start,
        end,
        html,
      }: {
        start: number
        end: number
        html: string
      }) => {
        editorRef.current?.setSelectionFromContribution({
          start,
          end,
          type: 0,
        })
        setSelection?.({
          start,
          end,
          html,
        })
      },
    }))

    /**
     * Not all the contributions are displayed on the editor
     */

    const editorContributions = useMemo(
      () =>
        contributions
          .filter(
            (contribution) =>
              contribution.contributionType !== ContributionType.OwnerEdit
          )
          .map((contribution) => ({
            status: contribution.status,
            id: contribution.id,
            start: contribution.begin,
            end: contribution.end,
            type:
              CONTRIBUTION_TYPE_MAP[
                contribution.contributionType as keyof typeof CONTRIBUTION_TYPE_MAP
              ] ?? 0,
          })),
      [contributions]
    )
    /**
     * Navigate among editor contribution
     */
    const selectContributionChange = (value: 1 | -1 | null) => {
      if (value === null) {
        setEditorContributionId('')
        return
      }
      editorRef.current?.selectContributionChange(value)
    }

    const onSelection = (selectionInfo: SelectionInfo) => {
      if (!perspective) {
        return
      }
      if (selectionInfo.end === 0) {
        setSelection?.(selectionInfo)
        return
      }
      if (selectionInfo.end - selectionInfo.start < 1) {
        setSelection?.(selectionInfo)
        return
      }

      /**
       * TODO: This format is better if is done in the editor it self
       */
      const selection = formatSelection(perspective.latestContent, {
        ...selectionInfo,
      })
      setEditorContributionId('')
      setSelection?.(selection)
    }

    /**
     * When the user select a contribution on editor or vertical indicator, we open the modal
     */
    const onContributionSelected = (contributionId: string | null) => {
      if (!contributionId) {
        setEditorContributionId('')
        return
      }

      const contribution = contributions.find((c) => c.id === contributionId)
      if (!contribution) {
        return
      }
      setEditorContributionId(contribution.id)
      onContribution?.(contribution.id)
    }

    /**
     * Update editor memory if new perspective content or contributions are loaded
     */
    useEffect(() => {
      if (!perspective) {
        return
      }
      editorRef.current?.setEditorContent(perspective?.latestContent || '')
      editorRef.current?.setContributions(editorContributions)
      editorRef.current?.showContributions(true)
    }, [perspective, editorContributions])

    console.log('EDITOR CONTRIBUTIONS!', {
      contributions: perspective?.contributions,
    })
    const contributionsToShow = (perspective?.contributions || []).filter(
      (contribution) =>
        ![ContributionStatus.Cancelled, ContributionStatus.Rejected].includes(
          contribution.status
        )
    )
    return (
      <>
        <Row style={{ alignItems: 'stretch', flex: 1 }}>
          <View
            style={{
              flexDirection: 'column',
              display: 'flex',
              flex: 1,
            }}
          >
            <ArticleEditor
              ref={editorRef}
              editorMode={'select'}
              onRefresh={refetch}
              onSelection={onSelection}
              onScrollEnd={setScrollPosition}
              onContribution={onContributionSelected}
              onContributionPositions={setContributionPositions}
              onEditorStateChange={onEditorStateChange}
            />
          </View>
          {displayVerticalIndicator && (
            <View
              style={{
                flexDirection: 'column',
                display: 'flex',
                width: 20,
                marginHorizontal: theme.spacing(2),
              }}
            >
              <VerticalContributionsIndicator
                onIndicatorPress={(contributions) => {
                  if (!contributions[0]?.id) {
                    return
                  }
                  setEditorContributionId(contributions[0]?.id)
                }}
                article={perspective as Article}
                scrollPosition={scrollPosition}
                heightOffset={
                  74 +
                  (editorContributionId || verticalIndicatorOffset ? 240 : 0)
                }
                contributions={contributionsToShow}
                positions={contributionPositions}
              />
            </View>
          )}
        </Row>
        {perspective && (
          <ContributionDetails
            readonly={readonly}
            perspective={perspective as Article}
            contributionId={editorContributionId || null}
            onClose={() => selectContributionChange(null)}
            onNext={() => selectContributionChange(1)}
            onPrevious={() => selectContributionChange(-1)}
            onFullScreen={() => onFullScreen?.(editorContributionId)}
          />
        )}
      </>
    )
  }
)

const useStyles = createStyle(({ theme }) => ({
  editor: {
    flex: 1,
    flexShrink: 1,
    backgroundColor: 'white',
    paddingHorizontal: theme.spacing(2),
  },
}))
